import React from 'react';

import { useBrand } from '@atc/brand';

import {
    Section,
} from 'reaxl';

import WarrantyModalContainer from '@/containers/vdp/WarrantyModalContainer';

import Card from './Card';
import CardContentWithAnalytics from './CardContentWithAnalytics';
import { carbStateWarrantyDisclaimer, cardHeaders, cardImages } from './staticContent';

function WarrantyCard({
    warranty,
    handleSeeDetailsClick = () => { },
    handleContentImpression = () => { },
    hasBatteryWarranty,
    hasFactoryWarranty,
    hasLimitedWarranty,
    isSafeGuardEnabled,
    listingId,
    ...rest
}) {
    const { brands, isBrand } = useBrand();
    const isAtc = isBrand(brands.ATC_BRAND);

    const { limitedWarranty, vehicleCoverage, originalWarranties = {} } = warranty ?? {};
    const limitedWarrantyData = limitedWarranty || vehicleCoverage;
    const { hybridElectricComponents, hybridElectricComponentsInCarbState, ...factoryWarranties } = originalWarranties;

    const batteryWarrantyContent = hasBatteryWarranty && {
        title: 'Factory Warranty',
        detail: `${hybridElectricComponents?.details} on Battery Warranty.`,
        modalData: {
            ...(hybridElectricComponents ? { hybridElectricComponents } : {}),
            ...(hybridElectricComponentsInCarbState ? { hybridElectricComponentsInCarbState } : {}),
        },
        warrantyType: 'Battery',
        disclaimer: !!hybridElectricComponentsInCarbState && carbStateWarrantyDisclaimer,
    };

    // Convert data for modal.
    const factoryContent = hasFactoryWarranty && {
        title: 'Factory Warranty',
        detail: `${factoryWarranties?.basic?.details} ${isSafeGuardEnabled ? "from the vehicle's original in-service date." : ''}`,
        modalData: { ...factoryWarranties },
        warrantyType: 'Factory',
        includesBatteryWarranty: hasBatteryWarranty,
    };

    // render modal data, the disclaimer takes precedence over the details if available
    const limitedWarrantyContent = hasLimitedWarranty && {
        title: 'Limited',
        detail: `${limitedWarrantyData.summary} ${isSafeGuardEnabled ? 'from purchase date. ' : ''}`,
        modalData: { ...limitedWarrantyData },
        warrantyType: 'Limited',
    };

    const warrantySummaryContent = ({ title, detail, modalData, warrantyType, disclaimer, includesBatteryWarranty }) => (
        <CardContentWithAnalytics
            cardType={warrantyType}
            handleContentImpression={handleContentImpression}
            listingId={listingId}
        >
            <Section
                alignment="left"
                uiContext="section-5"
                heading={hasLimitedWarranty && hasFactoryWarranty && title}
                className="margin-bottom-3"
            >
                {detail}
                {' '}
                {(modalData && Object.keys(modalData).length > 0) && (
                    <WarrantyModalContainer
                        warranty={{
                            warrantyData: modalData,
                            warrantyType,
                            disclaimer,
                        }}
                        handleCTAClick={handleSeeDetailsClick}
                        label="See details"
                    />
                )}
            </Section>
            {includesBatteryWarranty && (
                <>
                    {batteryWarrantyContent?.detail}
                    {batteryWarrantyContent?.modalData && (
                        <div>
                            <WarrantyModalContainer
                                warranty={{
                                    warrantyData: batteryWarrantyContent.modalData,
                                    warrantyType: batteryWarrantyContent.warrantyType,
                                    disclaimer: batteryWarrantyContent.disclaimer,
                                }}
                                handleCTAClick={handleSeeDetailsClick}
                                label="See details"
                            />
                        </div>
                    )}
                </>
            )}
        </CardContentWithAnalytics>
    );

    // if listing does not have multiple warranties then set heading to specific warranty
    let heading = cardHeaders.WARRANTY;
    if (!hasLimitedWarranty || !hasFactoryWarranty) {
        heading = (hasFactoryWarranty || hasBatteryWarranty) ? cardHeaders.FACTORY_WARRANTY : cardHeaders.LIMITED_WARRANTY;
    }

    return (
        <Card
            id="warranty"
            heading={heading}
            image={isAtc ? cardImages.WARRANTY_ATC : cardImages.WARRANTY}
            showTile
            {...rest}
        >
            {limitedWarrantyContent && warrantySummaryContent(limitedWarrantyContent)}
            {factoryContent && warrantySummaryContent(factoryContent)}
            {(!factoryContent && batteryWarrantyContent) && warrantySummaryContent(batteryWarrantyContent)}
        </Card>
    );
}

export default WarrantyCard;
